import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { isEmpty } from 'lodash';

import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import BobConfirmButton from '../../../components/BobConfirmButton';
import BobCancelButton from '../../../components/BobCancelButton';

import * as ConversionActions from '../../../actions/conversionActions';

import * as classes from './ManageConversionForm.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required(),
    rawDataType: Yup.string()
        .required(),
    displayDataType: Yup.string()
        .required(),
    defaultResult: Yup.string()
        .required()
});

const rawDataTypes = [
    {
        id:   'boolean',
        name: 'Boolean'
    },
    {
        id:   'integer',
        name: 'Integer'
    },
    {
        id:   'float',
        name: 'Float'
    },
    {
        id:   'text',
        name: 'Text'
    }
];

const ManageConversionForm = ({
    activeNavCompany,
    createConversion,
    deleteConversion,
    formRef,
    selectedConversion,
    setNewConversion,
    setSelectedConversion,
    updateConversion,
    ...props
}) => {
    const [conditions, setConditions] = useState([]);
    const [displayDataTypes, setDisplayDataTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isEmpty(selectedConversion)) {
            setConditions(selectedConversion.conditions);
            populateDisplayDataTypes(selectedConversion.rawDataType);
        } else {
            setConditions([]);
        }
    }, [selectedConversion]);

    const formik = useFormik({
        initialValues: {
            name:            !isEmpty(selectedConversion) ? selectedConversion.name : '',
            rawDataType:     !isEmpty(selectedConversion) ? selectedConversion.rawDataType : '',
            displayDataType: !isEmpty(selectedConversion) ? selectedConversion.displayDataType : '',
            defaultResult:   !isEmpty(selectedConversion) ? selectedConversion.defaultResult : '',
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            setIsLoading(true);


            try {
                const body = {
                    name:            values.name,
                    rawDataType:     values.rawDataType,
                    displayDataType: values.displayDataType,
                    conditions:      [...conditions],
                    defaultResult:   values.defaultResult
                };

                let created;

                if (isEmpty(selectedConversion)) {
                    created = await createConversion(activeNavCompany.id, body);
                } else {
                    created = await updateConversion(selectedConversion.id, activeNavCompany.id, body);
                }

                if (created) {
                    setSelectedConversion({});
                    setConditions([]);
                    setNewConversion(false);
                }

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        }
    });

    const populateDisplayDataTypes = value => {
        let values = [];
        switch (value) {
            case 'boolean':
            case 'integer':
                values.push(
                    {
                        id:   'boolean',
                        name: 'Boolean'
                    },
                    {
                        id:   'integer',
                        name: 'Integer'
                    }
                );
                break;
            case 'float':
                values.push(
                    {
                        id:   'boolean',
                        name: 'Boolean'
                    }
                );
                break;
            case 'text':
                values.push(
                    {
                        id:   'boolean',
                        name: 'Boolean'
                    },
                    {
                        id:   'integer',
                        name: 'Integer'
                    },
                    {
                        id:   'text',
                        name: 'Text'
                    }
                );
                break;
            default:
                break;
        }
        setDisplayDataTypes(values);
    };

    const handleRawDataTypeChange = event => {
        populateDisplayDataTypes(event.target.value);
        formik.setFieldValue('displayDataType', '', false);
        formik.setFieldValue('defaultResult', '', false);
        setConditions([]);
        formik.handleChange(event);
    };

    const handleDisplayDataTypeChange = event => {
        formik.setFieldValue('defaultResult', '', false);
        setConditions([]);
        formik.handleChange(event);
    };

    const renderDefaultResult = type => {
        const isKey2ActConversion = selectedConversion && selectedConversion.conversionType === 'Key2Act' ? true : false;

        switch (type) {
            case 'boolean':
                return (
                    <FormControl variant="outlined" size="small" className={classes.FormFields}>
                        <InputLabel
                            htmlFor="title"
                            error={Boolean(formik.errors.defaultResult) && Boolean(formik.touched.defaultResult)}
                        >
                            {<span>Default Result<span className={classes.Required}> *</span></span>}
                        </InputLabel>
                        <Select
                            disabled={isKey2ActConversion || !formik.values.displayDataType}
                            value={formik.values.defaultResult}
                            onChange={formik.handleChange}
                            className={classes.select}
                            inputProps={{
                                name:   'defaultResult',
                                id:     'defaultResult',
                                onBlur: formik.handleBlur
                            }}
                        >
                            <MenuItem
                                value={true}
                            >
                                True
                            </MenuItem>
                            <MenuItem
                                value={false}
                            >
                                False
                            </MenuItem>
                        </Select>
                    </FormControl>
                );
            case 'integer':
                return (
                    <FormControl className={classes.FormFields}>
                        <TextField
                            disabled={isKey2ActConversion || !formik.values.displayDataType}
                            id="defaultResult"
                            name="defaultResult"
                            type="number"
                            value={formik.values.defaultResult}
                            label={<span>Default Result<span className={classes.Required}> *</span></span>}
                            size="small"
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.defaultResult) && Boolean(formik.touched.defaultResult)}
                        />
                    </FormControl>
                );
            case 'text':
                return (
                    <FormControl className={classes.FormFields}>
                        <TextField
                            disabled={isKey2ActConversion || !formik.values.displayDataType}
                            id="defaultResult"
                            name="defaultResult"
                            type="text"
                            value={formik.values.defaultResult}
                            label={<span>Default Result<span className={classes.Required}> *</span></span>}
                            size="small"
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.defaultResult) && Boolean(formik.touched.defaultResult)}
                        />
                    </FormControl>
                );
            default:
                return (
                    <FormControl className={classes.FormFields}>
                        <TextField
                            disabled={isKey2ActConversion || !formik.values.displayDataType}
                            id="defaultResult"
                            name="defaultResult"
                            type="text"
                            value={formik.values.defaultResult}
                            label={<span>Default Result<span className={classes.Required}> *</span></span>}
                            size="small"
                            onChange={formik.handleChange}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                onBlur:  formik.handleBlur,
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                            FormHelperTextProps={{
                                classes: {
                                    error: classes.error
                                }
                            }}
                            error={Boolean(formik.errors.defaultResult) && Boolean(formik.touched.defaultResult)}
                        />
                    </FormControl>
                );
        }
    };

    const handleConditionChange = (e, index) => {
        const { value } = e.target;
        let updatedConditions = [...conditions];
        updatedConditions[index].condition = value;
        setConditions(updatedConditions);
    };

    const renderCondition = (type, index) => {
        const isKey2ActConversion = selectedConversion && selectedConversion.conversionType === 'Key2Act' ? true : false;

        switch (type) {
            case 'boolean':
                return (
                    <React.Fragment>
                        <InputLabel
                            htmlFor="condition"
                        >
                            {<span>Condition</span>}
                        </InputLabel>
                        <Select
                            value={conditions[index].condition}
                            onChange={(e) => handleConditionChange(e, index)}
                            className={classes.select}
                            disabled={isKey2ActConversion}
                            inputProps={{
                                name: 'condition',
                                id:   'condition',
                            }}
                        >
                            <MenuItem
                                value={true}
                            >
                                True
                            </MenuItem>
                            <MenuItem
                                value={false}
                            >
                                False
                            </MenuItem>
                        </Select>
                    </React.Fragment>
                );
            case 'integer':
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="condition"
                            name="condition"
                            type="number"
                            value={conditions[index].condition}
                            label={'Condition'}
                            size="small"
                            onChange={(e) => handleConditionChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
            case 'float':
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="condition"
                            name="condition"
                            type="number"
                            value={conditions[index].condition}
                            label={'Condition'}
                            size="small"
                            onChange={(e) => handleConditionChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
            case 'text':
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="condition"
                            name="condition"
                            type="text"
                            value={conditions[index].condition}
                            label={'Condition'}
                            size="small"
                            onChange={(e) => handleConditionChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
            default:
                break;
        }
    };

    const handleResultChange = (e, index) => {
        const { value } = e.target;
        let updatedConditions = [...conditions];
        updatedConditions[index].result = value;
        setConditions(updatedConditions);
    };

    const renderResult = (type, index) => {
        const isKey2ActConversion = selectedConversion && selectedConversion.conversionType === 'Key2Act' ? true : false;

        switch (type) {
            case 'boolean':
                return (
                    <React.Fragment>
                        <InputLabel
                            htmlFor="condition"
                        >
                            {<span>Result</span>}
                        </InputLabel>
                        <Select
                            value={conditions[index].result}
                            onChange={(e) => handleResultChange(e, index)}
                            className={classes.select}
                            disabled={isKey2ActConversion}
                            inputProps={{
                                name: 'result',
                                id:   'result',
                            }}
                        >
                            <MenuItem
                                value={true}
                            >
                                True
                            </MenuItem>
                            <MenuItem
                                value={false}
                            >
                                False
                            </MenuItem>
                        </Select>
                    </React.Fragment>
                );
            case 'integer':
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="result"
                            name="result"
                            type="number"
                            value={conditions[index].result}
                            label={'Result'}
                            size="small"
                            onChange={(e) => handleResultChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
            case 'text':
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="result"
                            name="result"
                            type="text"
                            value={conditions[index].result}
                            label={'Result'}
                            size="small"
                            onChange={(e) => handleResultChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
            default:
                return (
                    <React.Fragment>
                        <TextField
                            disabled={isKey2ActConversion}
                            id="result"
                            name="result"
                            type="text"
                            value={conditions[index].result}
                            label={'Result'}
                            size="small"
                            onChange={(e) => handleResultChange(e, index)}
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error:     classes.cssErrorUnderline,
                                    underline: classes.cssUnderline,
                                    root:      classes.input
                                }
                            }}
                            variant="outlined"
                        />
                    </React.Fragment>
                );
        }
    };

    const handleAddCondition = () => {
        let updatedConditions = [...conditions];
        updatedConditions.push({});
        setConditions(updatedConditions);
    };

    const handleRemoveConversion = index => {
        let updatedConditions = [...conditions];
        updatedConditions.splice(index, 1);

        setConditions(updatedConditions);
    };

    const handleResetConversion = () => {
        if (!isEmpty(selectedConversion)) {
            setConditions(selectedConversion.conditions);
            populateDisplayDataTypes(selectedConversion.rawDataType);

            formik.setFieldValue('name', selectedConversion.name, false);
            formik.setFieldValue('rawDataType', selectedConversion.rawDataType, false);
            formik.setFieldValue('displayDataType', selectedConversion.displayDataType, false);
            formik.setFieldValue('defaultResult', selectedConversion.defaultResult, false);
        } else {
            formik.setFieldValue('name', '', false);
            formik.setFieldValue('rawDataType', '', false);
            formik.setFieldValue('displayDataType', '', false);
            formik.setFieldValue('defaultResult', '', false);
        }
    };

    const handleDeleteConversion = async () => {
        const deleted = await deleteConversion(selectedConversion.id, activeNavCompany.id);

        if (deleted) {
            setConditions([]);
            setSelectedConversion({});
        }
    };

    formRef.handleDeleteConversion = handleDeleteConversion;
    formRef.handleResetConversion = handleResetConversion;

    const handleCancel = () => {
        setConditions([]);
        setSelectedConversion({});
    };

    return (
        <React.Fragment>
            <div className={classes.UpperPanel}>
                <div className={classes.Form}>
                    <div>
                        <FormControl className={classes.Name}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                disabled={(!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act')}
                                value={formik.values.name}
                                label={<span>Conversion Name<span className={classes.Required}> *</span></span>}
                                size="small"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                variant="outlined"
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                            />
                        </FormControl>
                    </div>
                    <FormControl variant="outlined" size="small" className={classes.FormFields}>
                        <InputLabel
                            htmlFor="rawDataType"
                            error={Boolean(formik.errors.rawDataType) && Boolean(formik.touched.rawDataType)}
                        >
                            {<span>Raw Data Type<span className={classes.Required}> *</span></span>}
                        </InputLabel>
                        <Select
                            disabled={!isEmpty(selectedConversion) || (!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act')}
                            value={formik.values.rawDataType}
                            id="rawDataType"
                            onChange={handleRawDataTypeChange}
                            name="rawDataType"
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.rawDataType) && Boolean(formik.touched.rawDataType)}
                        >
                            {
                                rawDataTypes.map(type => (
                                    <MenuItem
                                        key={type.id}
                                        value={type.id}
                                    >
                                        {type.name}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={classes.FormFields}>
                        <InputLabel
                            htmlFor="displayDataType"
                            error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                        >
                            {<span>Display Data Type<span className={classes.Required}> *</span></span>}
                        </InputLabel>
                        <Select
                            disabled={!isEmpty(selectedConversion) || (!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act') || !formik.values.rawDataType}
                            value={formik.values.displayDataType}
                            id="displayDataType"
                            onChange={handleDisplayDataTypeChange}
                            name="displayDataType"
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                        >
                            {
                                displayDataTypes.map(type => (
                                    <MenuItem
                                        key={type.id}
                                        value={type.id}
                                    >
                                        {type.name}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                    {renderDefaultResult(formik.values.displayDataType)}
                </div>
                <div className={classes.ConditionTitle}>
                    Conditions
                </div>
                <div className={classes.ConditionPanel}>
                    {
                        conditions.map((condition, index) => (
                            <div key={index}>
                                <FormControl variant="outlined" size="small" className={classes.Name}>
                                    {renderCondition(formik.values.rawDataType, index)}
                                </FormControl>
                                <FormControl variant="outlined" size="small" className={classes.FormFields}>
                                    {renderResult(formik.values.displayDataType, index)}
                                </FormControl>
                                {
                                    (!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act') ?
                                        null
                                        :
                                        <span role="none" onClick={() => handleRemoveConversion(index)} className={clsx('material-icons-outlined', classes.RemoveButton)}>
                                            close
                                        </span>
                                }
                            </div>
                        ))
                    }
                </div>
                {
                    (!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act') || !selectedConversion ?
                        null
                        :
                        <div
                            role="none"
                            onClick={(!formik.values.defaultResult && formik.values.defaultResult !== false) ? null : () => handleAddCondition()}
                            className={(!formik.values.defaultResult && formik.values.defaultResult !== false) ? classes.DisabledAddButton : classes.AddContainerButton}
                        >
                            <span className={clsx('material-icons-outlined', classes.AddButton)}>
                                add
                            </span>
                            <div className={classes.AddText}>Add Condition</div>
                        </div>
                }
            </div>
            <div className={classes.ButtonPanel}>
                <BobCancelButton
                    className={classes.CancelButton}
                    onClick={handleCancel}
                    disabled={isLoading}
                    label={'Cancel'}
                />
                <BobConfirmButton
                    className={classes.Buttons}
                    onClick={formik.handleSubmit}
                    disabled={!isEmpty(selectedConversion) && selectedConversion.conversionType.toLowerCase() === 'key2act'}
                    label={isEmpty(selectedConversion) ? 'Create' : 'Save'}
                    iconSize={15}
                    isLoading={isLoading}
                />
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => {
    return {
        activeNavCompany:   state.bob.activeNavCompany,
        customConversions:  state.conversion.customConversions,
        k2aConversions:     state.conversion.k2aConversions,
        selectedConversion: state.conversion.selectedConversion
    };
};

const mapDispatchToProps = {
    ...ConversionActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageConversionForm);
